import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Boxes, Search, Mail, Phone, Globe, UserCog } from "lucide-react";

const TPL_STATUSES = [["new", "Enquiry Received"], ["under_review", "Under Review"], ["expert_assigned", "Expert Assigned"], ["active_contract", "Active Contract"]];

export default function ThreePLEnquiriesPage() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const onSaved = (doc) => setRows((rs) => rs.map((r) => (r.id === doc.id ? doc : r)));

  useEffect(() => {
    api.get("/3pl/enquiries").then((r) => setRows(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const filtered = rows.filter((r) => {
    const s = q.toLowerCase().trim();
    if (!s) return true;
    return [r.full_name, r.company_name, r.work_email, r.phone, r.company_location].some((v) => String(v || "").toLowerCase().includes(s));
  });

  return (
    <div className="space-y-6" data-testid="threepl-enquiries-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading font-black text-2xl sm:text-4xl tracking-tight flex items-center gap-2"><Boxes className="h-7 w-7 text-primary" /> 3PL Enquiries</h1>
          <p className="text-muted-foreground mt-1 text-sm">Fulfilment leads submitted from the public "Talk to our Fulfillment Experts" form.</p>
        </div>
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input className="rounded-none pl-9" placeholder="Search name, company, email…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="threepl-search" />
        </div>
      </div>

      {loading ? (
        <div className="text-muted-foreground">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-border p-10 text-center text-muted-foreground">No 3PL enquiries yet.</div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden lg:block border border-border overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted text-[11px] uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="text-left px-3 py-2.5">Received</th>
                  <th className="text-left px-3 py-2.5">Name</th>
                  <th className="text-left px-3 py-2.5">Company</th>
                  <th className="text-left px-3 py-2.5">Contact</th>
                  <th className="text-left px-3 py-2.5">Model</th>
                  <th className="text-left px-3 py-2.5">Volume/mo</th>
                  <th className="text-left px-3 py-2.5">Mfg. Location</th>
                  <th className="text-left px-3 py-2.5">ARR</th>
                  <th className="text-left px-3 py-2.5">Needs</th>
                  <th className="text-left px-3 py-2.5">Status / Expert</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filtered.map((r) => (
                  <tr key={r.id} className="hover:bg-muted/40" data-testid={`threepl-row-${r.id}`}>
                    <td className="px-3 py-2.5 text-xs text-muted-foreground whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</td>
                    <td className="px-3 py-2.5 font-medium">{r.full_name}</td>
                    <td className="px-3 py-2.5">{r.company_name}<div className="text-xs text-muted-foreground">{r.company_location}</div></td>
                    <td className="px-3 py-2.5 text-xs">
                      <a href={`mailto:${r.work_email}`} className="text-primary hover:underline block">{r.work_email}</a>
                      <span className="text-muted-foreground">{r.phone}</span>
                      {r.website_url && <a href={r.website_url} target="_blank" rel="noreferrer" className="block text-muted-foreground hover:text-primary truncate max-w-[160px]">{r.website_url}</a>}
                    </td>
                    <td className="px-3 py-2.5">{r.business_model}</td>
                    <td className="px-3 py-2.5 font-mono-num">{r.order_volume}</td>
                    <td className="px-3 py-2.5">{r.manufacturing_location}</td>
                    <td className="px-3 py-2.5 font-mono-num">{r.annual_revenue || "—"}</td>
                    <td className="px-3 py-2.5 text-xs text-muted-foreground max-w-[220px]">{r.help_text || "—"}</td>
                    <td className="px-3 py-2.5"><StatusCell r={r} onEdit={() => setEditing(r)} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="lg:hidden space-y-3" data-testid="threepl-cards">
            {filtered.map((r) => (
              <div key={r.id} className="border border-border bg-card p-4" data-testid={`threepl-card-${r.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="font-bold">{r.full_name}</div>
                  <div className="text-[11px] text-muted-foreground">{new Date(r.created_at).toLocaleDateString()}</div>
                </div>
                <div className="text-sm">{r.company_name} · <span className="text-muted-foreground">{r.company_location}</span></div>
                <div className="mt-2 space-y-1 text-xs">
                  <div className="flex items-center gap-2"><Mail className="h-3.5 w-3.5 text-muted-foreground" /><a href={`mailto:${r.work_email}`} className="text-primary">{r.work_email}</a></div>
                  <div className="flex items-center gap-2"><Phone className="h-3.5 w-3.5 text-muted-foreground" />{r.phone}</div>
                  {r.website_url && <div className="flex items-center gap-2"><Globe className="h-3.5 w-3.5 text-muted-foreground" /><a href={r.website_url} target="_blank" rel="noreferrer" className="text-muted-foreground truncate">{r.website_url}</a></div>}
                </div>
                <div className="mt-2 flex flex-wrap gap-2 text-[11px]">
                  <span className="border border-border px-2 py-0.5">{r.business_model}</span>
                  <span className="border border-border px-2 py-0.5 font-mono-num">{r.order_volume}/mo</span>
                  <span className="border border-border px-2 py-0.5">Mfg: {r.manufacturing_location}</span>
                  {r.annual_revenue && <span className="border border-border px-2 py-0.5">ARR {r.annual_revenue}</span>}
                </div>
                {r.help_text && <div className="mt-2 text-xs text-muted-foreground">{r.help_text}</div>}
                <div className="mt-3 border-t border-border pt-3"><StatusCell r={r} onEdit={() => setEditing(r)} /></div>
              </div>
            ))}
          </div>
        </>
      )}
      <ManageDialog row={editing} onClose={() => setEditing(null)} onSaved={onSaved} />
    </div>
  );
}

function StatusCell({ r, onEdit }) {
  const label = (TPL_STATUSES.find(([k]) => k === (r.status || "new")) || TPL_STATUSES[0])[1];
  return (
    <div className="space-y-1">
      <div className="text-xs font-bold uppercase tracking-wide text-primary" data-testid={`threepl-status-${r.id}`}>{label}</div>
      {r.expert?.name && <div className="text-xs text-muted-foreground">{r.expert.name}{r.expert.phone ? ` · ${r.expert.phone}` : ""}</div>}
      <Button size="sm" variant="outline" className="rounded-none h-7 gap-1 text-xs" onClick={onEdit} data-testid={`threepl-manage-${r.id}`}><UserCog className="h-3.5 w-3.5" /> Manage</Button>
    </div>
  );
}

function ManageDialog({ row, onClose, onSaved }) {
  const [f, setF] = useState({ status: "new", expert_name: "", expert_phone: "", expert_email: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (row) setF({ status: row.status || "new", expert_name: row.expert?.name || "", expert_phone: row.expert?.phone || "", expert_email: row.expert?.email || "" });
  }, [row]);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/3pl/enquiries/${row.id}`, f);
      onSaved(data);
      toast.success("Enquiry updated");
      onClose();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  return (
    <Dialog open={!!row} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="rounded-none" data-testid="threepl-manage-dialog">
        <DialogHeader>
          <DialogTitle>Manage enquiry</DialogTitle>
          <DialogDescription>{row?.company_name} — status and assigned fulfillment expert are shown in the customer's dashboard.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <select value={f.status} onChange={set("status")} className="w-full h-10 border border-input bg-background px-3 text-sm" data-testid="threepl-status-select">
            {TPL_STATUSES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <Input className="rounded-none" placeholder="Expert name" value={f.expert_name} onChange={set("expert_name")} data-testid="threepl-expert-name" />
          <Input className="rounded-none" placeholder="Expert phone" value={f.expert_phone} onChange={set("expert_phone")} data-testid="threepl-expert-phone" />
          <Input className="rounded-none" placeholder="Expert email" value={f.expert_email} onChange={set("expert_email")} data-testid="threepl-expert-email" />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" className="rounded-none" onClick={onClose}>Cancel</Button>
            <Button className="rounded-none font-bold uppercase" disabled={busy} onClick={save} data-testid="threepl-save">{busy ? "Saving…" : "Save"}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
